import { CardElement, PlayStyle } from "../types/battle"

export type ElementAbilitySlot = 1 | 2 | 3

export type ElementAbilityEffectType =
  | "SELF_BS_PERCENT"
  | "ENEMY_BS_PERCENT"
  | "SELF_AND_ENEMY"
  | "SILENCE_SLOT"
  | "HALVE_ENEMY_ELEMENT_BONUS"
  | "CRIT_DAMAGE_DELTA"

export interface ElementAbilityDefinition {
  id: string
  element: CardElement
  slot: ElementAbilitySlot
  title: string
  description: string
  unlockRank: number
}

export const ELEMENT_ABILITY_UNLOCK_RANKS: Record<ElementAbilitySlot, number> =
  {
    1: 10,
    2: 16,
    3: 22,
  }

/** Mirror of Backend elementAbility.config — UI fallback if mechanics API missing. */
export const ELEMENT_ABILITIES: ElementAbilityDefinition[] = [
  {
    id: "EARTH_1_THICKET",
    element: "EARTH",
    slot: 1,
    title: "Заросли",
    description: "Враг −8% БС",
    unlockRank: 10,
  },
  {
    id: "EARTH_2_LOG",
    element: "EARTH",
    slot: 2,
    title: "Бревно",
    description: "+10% БС",
    unlockRank: 16,
  },
  {
    id: "EARTH_3_GOLEM",
    element: "EARTH",
    slot: 3,
    title: "Каменный голем",
    description: "+8% БС; на DEFENSE ещё +4%",
    unlockRank: 22,
  },
  {
    id: "WIND_1_HURRICANE",
    element: "WIND",
    slot: 1,
    title: "Ураган",
    description: "Враг не может применить способность слота I",
    unlockRank: 10,
  },
  {
    id: "WIND_2_TORNADO",
    element: "WIND",
    slot: 2,
    title: "Торнадо",
    description: "Враг −8% БС",
    unlockRank: 16,
  },
  {
    id: "WIND_3_WHIRLWIND",
    element: "WIND",
    slot: 3,
    title: "Вихрь",
    description: "+10% БС; на SPEED ещё +3%",
    unlockRank: 22,
  },
  {
    id: "FIRE_1_SPEAR",
    element: "FIRE",
    slot: 1,
    title: "Огненное копьё",
    description: "Враг −6% БС",
    unlockRank: 10,
  },
  {
    id: "FIRE_2_ARMOR",
    element: "FIRE",
    slot: 2,
    title: "Огненная броня",
    description: "+8% БС; +11% если карта огня",
    unlockRank: 16,
  },
  {
    id: "FIRE_3_FLAME",
    element: "FIRE",
    slot: 3,
    title: "Пламя",
    description: "Бонус стихии врага ×0.5",
    unlockRank: 22,
  },
  {
    id: "WATER_1_FLOOD",
    element: "WATER",
    slot: 1,
    title: "Наводнение",
    description: "Враг не может применить способность слота II",
    unlockRank: 10,
  },
  {
    id: "WATER_2_TRIDENT",
    element: "WATER",
    slot: 2,
    title: "Трезубец",
    description: "ATTACK: +12% БС; иначе +6%",
    unlockRank: 16,
  },
  {
    id: "WATER_3_TSUNAMI",
    element: "WATER",
    slot: 3,
    title: "Цунами",
    description: "DEFENSE: +10% тебе и враг −8%; ATTACK: +14%; SPEED: +7%",
    unlockRank: 22,
  },
  {
    id: "LIGHTNING_1_BOLT",
    element: "LIGHTNING",
    slot: 1,
    title: "Молния",
    description: "При крите пассива +12% БС от базы",
    unlockRank: 10,
  },
  {
    id: "LIGHTNING_2_STORM",
    element: "LIGHTNING",
    slot: 2,
    title: "Гроза",
    description: "Враг −8% БС",
    unlockRank: 16,
  },
  {
    id: "LIGHTNING_3_BALL",
    element: "LIGHTNING",
    slot: 3,
    title: "Шаровая молния",
    description: "+12% БС",
    unlockRank: 22,
  },
  {
    id: "ICE_1_BARRIER",
    element: "ICE",
    slot: 1,
    title: "Ледяной барьер",
    description: "Враг −8% БС (с пассивом — кап −12%)",
    unlockRank: 10,
  },
  {
    id: "ICE_2_SNOW_GOLEM",
    element: "ICE",
    slot: 2,
    title: "Снежный голем",
    description: "+10% БС",
    unlockRank: 16,
  },
  {
    id: "ICE_3_AVALANCHE",
    element: "ICE",
    slot: 3,
    title: "Лавина",
    description: "Враг −12% БС (с пассивом — кап −12%)",
    unlockRank: 22,
  },
]

const BY_ID = new Map(ELEMENT_ABILITIES.map((a) => [a.id, a]))

export function getElementAbilityById(
  id: string | null | undefined
): ElementAbilityDefinition | null {
  if (!id) return null
  return BY_ID.get(id) ?? null
}

export function getElementAbilitiesFor(
  element: CardElement | null | undefined
): ElementAbilityDefinition[] {
  if (!element) return []
  return ELEMENT_ABILITIES.filter((a) => a.element === element)
}

export function getUnlockedElementAbilities(
  element: CardElement | null | undefined,
  guardianRank: number
): ElementAbilityDefinition[] {
  return getElementAbilitiesFor(element).filter(
    (a) => guardianRank >= a.unlockRank
  )
}

export function isAbilityUnlocked(
  ability: ElementAbilityDefinition,
  guardianRank: number
): boolean {
  return guardianRank >= ability.unlockRank
}

/** Rough self-buff preview % for UI (not full resolve). */
export function previewAbilitySelfPercent(
  abilityId: string | null | undefined,
  cardStyle: PlayStyle,
  cardElement: CardElement
): number {
  const ability = getElementAbilityById(abilityId)
  if (!ability) return 0
  switch (ability.id) {
    case "EARTH_2_LOG":
      return 0.1
    case "EARTH_3_GOLEM":
      return cardStyle === "DEFENSE" ? 0.12 : 0.08
    case "WIND_3_WHIRLWIND":
      return cardStyle === "SPEED" ? 0.13 : 0.1
    case "FIRE_2_ARMOR":
      return cardElement === "FIRE" ? 0.11 : 0.08
    case "WATER_2_TRIDENT":
      return cardStyle === "ATTACK" ? 0.12 : 0.06
    case "WATER_3_TSUNAMI":
      if (cardStyle === "ATTACK") return 0.14
      if (cardStyle === "DEFENSE") return 0.1
      return 0.07
    case "LIGHTNING_3_BALL":
      return 0.12
    case "ICE_2_SNOW_GOLEM":
      return 0.1
    default:
      return 0
  }
}

export type { PlayStyle }
